import { useMemo, forwardRef, RefObject, useCallback, useImperativeHandle, useRef, useEffect } from 'react';
import { Formik, Form, Field, FormikProps, useFormikContext } from 'formik';
import { queueAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import { LegType, legTypeHasContainer, noContainerReason } from '@api/Trinium/types';
import { PatchLegPayload } from '@api/Trinium/Endpoints/Leg';
import { resolveCopyCntrChassisToAllLegs } from '@api/Trinium/Endpoints/Leg/actions';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { usePatchLegsMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { EditableCellRef, EditSaveScope, defaultSortComparator } from '@components/DataGrid/types';
import RenderHeader from '@components/DataGrid/RenderHeader';
import { HeaderSubLabel, SubColumnDef, subColumnField } from '@components/DataGrid/subColumns';
import { EditableCell, resolveEditTargetRows } from '@components/DataGrid/Dispatch/types';
import DisabledTooltip from '@components/DisabledTooltip';
import ContainerTypeDropdownField from '@forms/Fields/ContainerTypeDropdown';
import ContainerSizeDropdownField from '@forms/Fields/ContainerSizeDropdown';
import { FormikTextInput } from '@forms/Components/TextInput';
import { FormikNumberInput } from '@forms/Components/NumberInput';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch, useAppSelector } from '@reduxStore';
import Container from '../Cells/Container';
import { applyColumnQuickFilter, caseInsensitiveIncludes } from '../columns';
import classNames from 'classnames/bind';
import { formatEquipmentValue } from '@forms/Fields/EquipmentField';
import { GridColDefExtended } from '@mui/x-data-grid-pro';
import * as Yup from 'yup';

import styles from '../DataGrid.Dispatch.module.css';
const styleContext = classNames.bind(styles);

/**
 * Allows searching "ABCD1234" with values like "ABCD 1234", "ABCD-1234", etc.
 */
export const normalizeContainerString = (str: string | undefined): string => str?.replace(/[\s-]/g, '').toLowerCase() ?? '';

const containerField = 'Container';
const headerDisplayName = 'Container';

/** Sub-values of the Container column sortable via hidden companion columns */
const containerSubColumns: SubColumnDef<DispatchLeg>[] = [
  { key: 'size', label: 'Size', valueGetter: (row) => row.cntrSize },
  { key: 'type', label: 'Type', valueGetter: (row) => row.cntrType },
  { key: 'weight', label: 'Weight (lbs)', valueGetter: (row) => row.wgtLb }
];

const containerSubLabels: HeaderSubLabel[] = containerSubColumns.map((sub) => ({
  label: sub.label,
  field: subColumnField(containerField, sub.key)
}));

const containerDefinition: GridColDefExtended<DispatchLeg> = {
  field: containerField,
  minWidth: 50,
  width: 148,
  headerName: 'Container #|||Size|||Type|||Weight',
  headerDisplayName,
  subColumns: containerSubColumns,
  /** Mobile cards invoke renderHeader without params (RenderCardCell), so they fall back to the
   * static secondaryText instead of mounting the grid-subscribing sort labels per card */
  renderHeader: (params) => (
    <RenderHeader
      primaryText={headerDisplayName}
      subLabels={params.field ? containerSubLabels : undefined}
      secondaryText='Size, Type, Weight (lbs)'
    />
  ),
  renderCell: ({ row, colDef }) => (
    <Container
      row={row}
      colDef={colDef}
    />
  ),
  disableReorder: true,
  getApplyQuickFilterFn: (value: string) => {
    return (_, row): boolean =>
      applyColumnQuickFilter(value, row, (value: string, row: DispatchLeg) => {
        const { cntrId, cntrSize, cntrType, totWgt } = row;
        const normalizedValue = normalizeContainerString(value);
        const normalizedCntrId = normalizeContainerString(cntrId);
        return (
          normalizedCntrId.includes(normalizedValue) ||
          caseInsensitiveIncludes(value, [cntrType]) ||
          caseInsensitiveIncludes(value, [cntrSize, totWgt])
        );
      });
  },
  valueGetter: (_, row: DispatchLeg) =>
    `${row.cntrId ?? ''}|||${String(row.cntrSize ?? '')}|||${row.cntrType ?? ''}|||${String(row.totWgt ?? '')}`,
  sortable: true,
  sortComparator: (v1, v2, cellParams1) => {
    if (typeof v1 === 'string' && typeof v2 === 'string') {
      return defaultSortComparator(v1.split('|||')[0], v2.split('|||')[0], cellParams1);
    }

    return 0;
  },
  renderEditableCell: (row: DispatchLeg, ref: RefObject<EditableCellRef | null>, selectedRows?: DispatchLeg[]) => (
    <ContainerEditCell
      row={row}
      selectedRows={selectedRows}
      ref={ref}
    />
  )
};

interface ContainerFormValues {
  cntrId?: string;
  cntrSize?: string;
  cntrType?: string;
  wgtLb?: number;
}

type ContainerBatchPatch = Partial<Pick<PatchLegPayload, 'cntrId' | 'cntrSize' | 'cntrType' | 'wgtLb'>>;

/**
 * Component to render when the Container cell is clicked and enters edit mode
 */
const ContainerEditCell = forwardRef<EditableCellRef, EditableCell<DispatchLeg>>(({ row, selectedRows, onValidityChange }, ref) => {
  const { number: terminalNumber } = useCurrentTerminal();
  const appDispatch = useAppDispatch();
  const [patchLegs] = usePatchLegsMutation();
  const formikRef = useRef<FormikProps<ContainerFormValues>>(null);
  const isLoading = useAppSelector((state) => state.dispatchBoardState.loadingSet.includes(row.legId));

  const isFCL = useMemo(() => row.legType && LegType[row.legType] === LegType.FCL, [row.legType]);
  const hasContainer = useMemo(() => legTypeHasContainer(row.legType), [row.legType]);

  const initialValues: ContainerFormValues = useMemo(() => {
    return {
      cntrId: row.cntrId ?? '',
      cntrSize: String(row.cntrSize),
      cntrType: row.cntrType,
      wgtLb: row.wgtLb ? row.wgtLb : undefined
    };
  }, [row]);

  const saveHandler = useCallback(
    async (values: ContainerFormValues, scope: EditSaveScope) => {
      const legValuesToPost: ContainerBatchPatch = {};

      if (values.cntrId !== initialValues.cntrId) {
        legValuesToPost.cntrId = values.cntrId;
      }
      if (values.wgtLb !== initialValues.wgtLb) {
        legValuesToPost.wgtLb = values.wgtLb;
      }
      if (values.cntrSize !== initialValues.cntrSize || values.cntrType !== initialValues.cntrType) {
        legValuesToPost.cntrSize = values.cntrSize ? values.cntrSize : initialValues.cntrSize;
        legValuesToPost.cntrType = values.cntrType ? values.cntrType : initialValues.cntrType;
      }

      if (Object.keys(legValuesToPost).length > 0) {
        const cpyCntrChassisToAllLegs = await resolveCopyCntrChassisToAllLegs(row.isLive, legValuesToPost.cntrId, initialValues.cntrId, 'container number');
        const targetRows = resolveEditTargetRows(scope, row, selectedRows, (targetRow) => legTypeHasContainer(targetRow.legType));

        await patchLegs({
          terminalNumber,
          legIds: targetRows.map((targetRow) => targetRow.legId),
          leg: {
            ...legValuesToPost,
            ...(cpyCntrChassisToAllLegs !== undefined && { cpyCntrChassisToAllLegs })
          }
        })
          .unwrap()
          .then((response) => {
            response.errors.forEach((error) => {
              appDispatch(
                queueAnnouncement({
                  title: error.errorMessage,
                  severity: 'error',
                  clearable: true
                })
              );
            });
          })
          .catch((error: unknown) => {
            appDispatch(
              queueAnnouncement({
                title: getApiErrorMessage(error),
                severity: 'error',
                clearable: true
              })
            );
          });
      }
    },
    [appDispatch, terminalNumber, patchLegs, row, selectedRows, initialValues]
  );

  useImperativeHandle(
    ref,
    () => ({
      saveChanges: (scope: EditSaveScope) => {
        const formik = formikRef.current;
        if (!formik) {
          return;
        }
        void formik.validateForm().then((errors) => {
          if (Object.keys(errors).length === 0) {
            void saveHandler(formik.values, scope);
          }
        });
      }
    }),
    [saveHandler]
  );

  return (
    <Formik
      initialValues={initialValues}
      innerRef={formikRef}
      validationSchema={Yup.object({
        wgtLb: Yup.number().nullable().max(99999, 'Weight (lbs) cannot exceed 99,999')
      })}
      onSubmit={() => {
        /* no-op */
      }}>
      {({ setFieldValue }) => (
        <Form className={styles['container-edit-form']}>
          <ValidityReporter onValidityChange={onValidityChange} />
          <Field
            name='cntrId'
            label='Container #'
            component={FormikTextInput}
            variant='standard'
            onBlurExtension={(event: React.FocusEvent<HTMLInputElement>) => {
              const formattedValue = formatEquipmentValue(event.target.value);
              void setFieldValue('cntrId', formattedValue);
            }}
            disabled={!hasContainer || isLoading}
            disabledReason={!hasContainer ? noContainerReason : isLoading ? 'Updating...' : undefined}
          />
          <div className={styles['container-edit-row']}>
            <ContainerSizeDropdownField
              fieldName='cntrSize'
              className={styleContext({
                'container-edit-row-item': true,
                size: true
              })}
              isDisabled={!isFCL || isLoading}
              disabledReason={!isFCL ? nonFCLDisabledReason : isLoading ? 'Updating...' : undefined}
            />
            <ContainerTypeDropdownField
              fieldName='cntrType'
              className={styleContext({
                'container-edit-row-item': true,
                type: true
              })}
              isDisabled={!isFCL || isLoading}
              disabledReason={!isFCL ? nonFCLDisabledReason : isLoading ? 'Updating...' : undefined}
            />
            <DisabledTooltip message={!isFCL ? nonFCLDisabledReason : isLoading ? 'Updating...' : undefined}>
              <Field
                name='wgtLb'
                label='Weight (lb)'
                component={FormikNumberInput}
                variant='standard'
                className={styleContext({
                  'container-edit-row-item': true
                })}
                disabled={!isFCL || isLoading}
              />
            </DisabledTooltip>
          </div>
        </Form>
      )}
    </Formik>
  );
});

/** Bridges Formik's validity into the editable container so it can gate the save buttons */
const ValidityReporter = ({ onValidityChange }: { onValidityChange?: (isValid: boolean) => void }) => {
  const { isValid } = useFormikContext<ContainerFormValues>();

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  return null;
};

const nonFCLDisabledReason = 'Editing disabled for non-FCL legs';

export default containerDefinition;
